/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import { nanoid } from "nanoid";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { ContactFilterAtom } from "../../../atoms/ContactFilterAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import useMediaQuery from "../../../hook/useMediaQuery";
import { ICategory } from "../../../types/CategoryTypes";
import { IOptionValues } from "../../../types/TagQueryTypes";
import LoadingAbsolute from "../../LoadingAbsolute";
import StyledCheckBox from "../../StyledCheckBox";
import CategoryActions from "./CategoryActions";

interface TableProps {
  check: boolean;
}

const Row = styled(motion.div)<TableProps>`
  display: grid;
  border: 1px solid;
  border-color: ${(props) => (props.check ? "#FB972E" : "#eaeaeabf")};
  background-color: ${(props) =>
    props.check ? "rgb(251, 151, 46, 0.05)" : "#ffffff"};
  grid-template-rows: 1fr;
  align-items: center;
`;

const GridRow = styled(motion.div)`
  width: 100%;
  height: 60px;

  background-color: #ffffff;

  display: grid;
  grid-template-columns:
    100px minmax(120px, 160px) 60px minmax(110px, 180px)
    60px;
  grid-column-gap: 24px;

  padding: 15px 25px;

  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 685px) {
    width: fit-content;
  }
`;

const TableTitle = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 16px;
  align-items: center;
  overflow: hidden;
`;

const TableActionsRow = styled.div`
  color: #2f2f2f;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: flex-end;
  margin-right: 10px;
  cursor: pointer;
`;

const marginRight15 = css`
  margin-right: 15px;
`;

const marginRight30 = css`
  margin-right: 30px;
`;

const Name = styled.span`
  color: #2f2f2f;
`;

const TextCss = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

const userCss = css`
  ${TextCss}
  cursor: pointer;
  :hover {
    opacity: 0.6;
  }
`;

const WrapperCheckboxAndTitle = styled.div`
  display: flex;
  align-content: center;
  justify-content: start;
`;

interface Props {
  row: ICategory & {
    tagsAmount: number;
  };
  isChecked: boolean;
  handleCheck: () => void;
}

export default function CategoriesTableRow(props: Props): JSX.Element {
  const { row, handleCheck, isChecked } = props;
  const isLargerThan1200 = useMediaQuery("(min-width: 1200px)");
  const setContactFilter = useSetRecoilState(ContactFilterAtom);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const tags = useRecoilValue(TagsAtom);

  async function filterContact() {
    if (isLoading) return;
    setIsLoading(true);

    const tagsOfCategory = tags.filter((tag) => tag.categoryId === row._id);

    if (tagsOfCategory.length === 0) return;

    const tagsId = tagsOfCategory.map((tag) => tag._id);

    const tagQuery = {
      operator: "ANY_OF" as IOptionValues,
      tags: [...tagsId],
      next: null,
    };

    setContactFilter({
      withTags: [...tagsOfCategory],
      withoutTag: [],
      allOfTags: [],
      tagQuery: tagQuery,
      recipientTags: [
        {
          option: {
            name: "With any of these tags",
            value: "ANY_OF",
          },
          logic: { value: "AND", name: "And" },
          tags: [...tagsOfCategory],
          id: nanoid(),
        },
      ],
    });

    router.push("/contacts?fromTag=true");

    setIsLoading(false);
  }

  return (
    <Row
      check={isChecked}
      layout
      css={css`
        margin-top: 7px;
      `}
    >
      {isLoading && <LoadingAbsolute />}

      <AnimatePresence>
        <GridRow layout>
          <WrapperCheckboxAndTitle>
            <StyledCheckBox
              customCss={marginRight30}
              check={isChecked}
              handleCheck={handleCheck}
            />
            <TableTitle>{row.type}</TableTitle>
          </WrapperCheckboxAndTitle>
          <TableTitle>
            <Name
              css={css`
                ${TextCss}
              `}
            >
              {row.name}
            </Name>
          </TableTitle>

          <TableTitle
            css={css`
              ${userCss}
            `}
            onClick={() => filterContact()}
          >
            {row.tagsAmount}
          </TableTitle>

          <TableTitle>
            {isLargerThan1200
              ? dayjs((row as any).createdAt).format("MMM D, YYYY [@] h:mm a")
              : dayjs((row as any).createdAt).format("MM/DD/YYYY")}
          </TableTitle>

          <TableActionsRow>
            {row.type !== "system" && <CategoryActions category={row} />}
          </TableActionsRow>
        </GridRow>
      </AnimatePresence>
    </Row>
  );
}
